import React, { useEffect, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../../master/Master";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { formatToDate } from "../../master/FormatToDateTime";
import usePrivateLink from "../../config/usePrivateLink";
import { studentDetailsV4 } from "../../../api/Student";
import { imgBaseUrl } from "../../../api/api";

import FeeTransactions from "../tabs/FeeTransactions";
import MarkReports from "../tabs/MarkReports";
import AttendanceCalendar from "../tabs/Attendance";
import Master from "../Master";
import StudentAdmissionForm from "../tabs/StudentAdmissionForm";
import StudentDemoForm from "../tabs/StudentDemoForm";
import { getDetails } from "../../../api/HostelService";
import CourseNFeeTab from "../../hostel/v2/CourseNFeeTab";
import CourseNFees from "../../student_v2/CourseNFees";
import BasicDetailsV4 from "./BasicDetailsV4";

/* ─── Small helper: profile info row (Bootstrap-only, no custom CSS) ─── */
function InfoRow({ icon, iconBg, iconColor, label, value, onClick, clickable }) {
  return (
    <div
      className="d-flex align-items-start gap-3 py-2 border-bottom"
      style={{ cursor: clickable ? "pointer" : "default" }}
      onClick={onClick}
    >
      <div
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 34, height: 34, borderRadius: "9px", background: iconBg }}
      >
        <i className={icon} style={{ color: iconColor, fontSize: "1rem" }} />
      </div>
      <div className="overflow-hidden">
        <div className="text-muted text-uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.03em" }}>
          {label}
        </div>
        <div className="fw-semibold text-dark text-truncate" style={{ fontSize: "0.86rem" }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

/* ─── Small helper: quick-action button ───────────────────────────────── */
function ActionButton({ to, icon, label, variant = "outline-secondary", full }) {
  return (
    <div className={full ? "col-12" : "col-6"}>
      <Link
        to={to}
        className={`btn btn-${variant} btn-sm fw-semibold w-100 d-flex align-items-center justify-content-center gap-1 py-2`}
        style={{ borderRadius: "10px" }}
      >
        <i className={`${icon} ri-18px`} />
        <span className="text-truncate">{label}</span>
      </Link>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────── */
export default function StudentDetailsV4() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get("id") ?? "";

  const [data, setData] = useState({});
  const type = params.get("type") ?? "BasicDetails";
  const [hostelData, setHostelData] = useState(null);

  const [filters, setFilters] = useState({
    type: type,
    student_batch_n_fee_id: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("type", filters?.type);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [filters?.type]);

  useEffect(() => {
    if (filters?.type === "Hostel" && data?.hostel_student_id) {
      getHostelData();
    }
  }, [filters?.type, data?.hostel_student_id]);

  const getHostelData = async () => {
    try {
      const response = await getDetails(data?.hostel_student_id);
      if (response?.data?.status) {
        setHostelData(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const TABS = [
    { icon: "ri-user-3-line", label: "Basic Details", link: "BasicDetails" },
    { icon: "ri-book-open-line", label: "Course & Fees", link: "CourseNFees" },
    { icon: "ri-bar-chart-grouped-line", label: "Mark Report", link: "MarkReport" },
    { icon: "ri-calendar-check-line", label: "Attendance", link: "Attendance" },
    { icon: "ri-file-list-3-line", label: "Admission Form", link: "AdmissionForm" },
    { icon: "ri-clipboard-line", label: "Demo Form", link: "DemoForm" },

    ...(data?.hostel_student_id
      ? [{ icon: "ri-hotel-bed-line", label: "Hostel", link: "Hostel" }]
      : []),

    ...(data?.library_student_id
      ? [
        {
          icon: "ri-book-2-line",
          label: "Library",
          link: `/library/student/profile?id=${data?.library_student_id}`,
          isRedirect: true,
        },
      ]
      : []),
    ...(data?.bus_student_id
      ? [{ icon: "ri-bus-2-line", label: "Bus", link: "Bus" }]
      : []),
  ];

  const getDataList = async () => {
    setLoading(true);
    let addUrl = `id=${id}`;
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
    });
    try {
      const response = await studentDetailsV4(addUrl);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDataList();
  }, [filters]);

  /* Avatar fallback */
  const [imgError, setImgError] = useState(false);
  const initials = data?.name ? data.name.charAt(0).toUpperCase() : "?";

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row g-2">
                  <Master />

                  <div className="col-12">
                    <div className="row g-4">
                      {/* ════════════════════════════════════════
                          LEFT — Profile card
                      ════════════════════════════════════════ */}
                      <div className="col-xl-3 col-lg-4 col-md-5">
                        <div
                          className="card border-0 shadow-sm d-flex flex-column"
                          style={{ height: "calc(100vh - 120px)", borderRadius: "16px", overflow: "hidden" }}
                        >
                          {/* FIXED TOP */}
                          <div>
                            {/* Banner */}
                            <div
                              className="bg-primary bg-gradient"
                              style={{ height: "84px" }}
                            />

                            {/* Avatar + name */}
                            <div className="px-3 pt-3">
                              <div className="d-flex justify-content-center mb-2" style={{ marginTop: "-56px" }}>
                                <div style={{ position: "relative" }}>
                                  {data?.profile_picture && !imgError ? (
                                    <img
                                      src={imgBaseUrl + data.profile_picture}
                                      alt="avatar"
                                      onError={() => setImgError(true)}
                                      className="border border-4 border-white shadow-sm"
                                      style={{
                                        width: 96,
                                        height: 96,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center border border-4 border-white shadow-sm bg-label-primary text-primary fw-bold"
                                      style={{ width: 96, height: 96, borderRadius: "50%", fontSize: "2rem" }}
                                    >
                                      {initials}
                                    </div>
                                  )}
                                  <span
                                    className="position-absolute bottom-0 end-0 bg-success border border-2 border-white rounded-circle"
                                    style={{ width: 16, height: 16 }}
                                  />
                                </div>
                              </div>

                              <div className="text-center mb-2">
                                <h5 className="fw-bold mb-0" style={{ fontSize: "1.05rem" }}>
                                  {data?.name || "—"}
                                </h5>

                                <div className="text-muted small mb-2">
                                  <i className="ri-shield-keyhole-line me-1" />
                                  {data?.registration_no || "—"}
                                </div>

                                <div className="d-flex justify-content-center gap-1 flex-wrap">
                                  {data?.category && (
                                    <span className="badge rounded-pill bg-label-primary">
                                      {data.category}
                                    </span>
                                  )}
                                  {data?.gender && (
                                    <span className="badge rounded-pill bg-label-success">
                                      {data.gender}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <hr className="my-2" />
                            </div>
                          </div>

                          {/* SCROLLABLE INFO */}
                          <div className="flex-grow-1 overflow-auto px-3" style={{ minHeight: 0 }}>
                            <InfoRow
                              icon="ri-user-3-line"
                              iconBg="#eff6ff"
                              iconColor="#1a73e8"
                              label="Father's Name"
                              value={data?.father_name}
                            />
                            <InfoRow
                              icon="ri-user-heart-line"
                              iconBg="#fdf4ff"
                              iconColor="#9333ea"
                              label="Mother's Name"
                              value={data?.mother_name}
                            />
                            <InfoRow
                              icon="ri-mail-line"
                              iconBg="#f0fdf4"
                              iconColor="#16a34a"
                              label="Email"
                              value={data?.email}
                            />
                            <InfoRow
                              icon="ri-phone-line"
                              iconBg="#fff7ed"
                              iconColor="#ea580c"
                              label="Mobile"
                              value={data?.phone}
                            />
                            <InfoRow
                              icon="ri-smartphone-line"
                              iconBg="#eff6ff"
                              iconColor="#2563eb"
                              label="Secondary Mobile"
                              value={data?.secondary_mobile}
                            />
                            <InfoRow
                              icon="ri-cake-3-line"
                              iconBg="#fef9c3"
                              iconColor="#ca8a04"
                              label="Date of Birth"
                              value={formatToDate(data?.dob)}
                            />
                            <InfoRow
                              icon="ri-calendar-event-line"
                              iconBg="#ecfdf5"
                              iconColor="#059669"
                              label="Admission Date"
                              value={formatToDate(data?.admission_date)}
                            />
                            <InfoRow
                              icon="ri-map-pin-line"
                              iconBg="#fff1f2"
                              iconColor="#e11d48"
                              label="Address"
                              value={[data?.village, data?.district, data?.state, data?.zipcode]
                                .filter(Boolean)
                                .join(", ")}
                            />
                            <InfoRow
                              icon="ri-fingerprint-line"
                              iconBg="#f0fdf4"
                              iconColor="#15803d"
                              label="Aadhar"
                              value={data?.aadhar}
                            />
                            <InfoRow
                              icon="ri-briefcase-line"
                              iconBg="#eef2ff"
                              iconColor="#6366f1"
                              label="Father Occupation"
                              value={data?.father_occupation}
                            />
                            <InfoRow
                              icon="ri-graduation-cap-line"
                              iconBg="#ecfeff"
                              iconColor="#06b6d4"
                              label="Education"
                              value={
                                Array.isArray(data?.education)
                                  ? data.education?.map((e) => e.name).join(", ")
                                  : data?.education
                              }
                            />
                            <InfoRow
                              icon="ri-book-line"
                              iconBg="#fef3c7"
                              iconColor="#f59e0b"
                              label="Medium"
                              value={data?.medium}
                            />
                            <InfoRow
                              icon="ri-hotel-bed-line"
                              iconBg="#f0f9ff"
                              iconColor="#0ea5e9"
                              label="Hostel"
                              value={data?.hostel_student_id ? "Yes" : data?.hostel_status || "No"}
                              clickable={!!data?.hostel_student_id}
                              onClick={() => {
                                if (data?.hostel_student_id) {
                                  navigate(`/hostel/profile?id=${data.hostel_student_id}`);
                                }
                              }}
                            />
                            <InfoRow
                              icon="ri-bus-line"
                              iconBg="#ecfdf5"
                              iconColor="#10b981"
                              label="Bus"
                              value={data?.bus_status}
                            />
                          </div>

                          {/* FIXED BUTTONS */}
                          <div className="p-3 border-top">
                            <div className="row g-2">
                              {hasAccess("/student/update") && (
                                <ActionButton
                                  to={`/student/update?id=${data?.id}`}
                                  icon="ri-pencil-line"
                                  label="Edit Profile"
                                  variant="primary"
                                />
                              )}
                              {hasAccess("/student/fees/deposit") && (
                                <ActionButton
                                  to={`/student/fees/deposit?id=${data?.id}`}
                                  icon="ri-money-rupee-circle-line"
                                  label="Pay Fees"
                                />
                              )}
                              {hasAccess("/student/batch/enroll") && (
                                <ActionButton
                                  to={`/student/batch/enroll?student_id=${data?.id}`}
                                  icon="ri-group-line"
                                  label="Add in Batch"
                                />
                              )}
                              {hasAccess("/student/bus/enroll") && (
                                <ActionButton
                                  to={`/student/bus/enroll?student_id=${data?.id}`}
                                  icon="ri-bus-line"
                                  label="Add to Bus"
                                />
                              )}
                              {hasAccess("/student/documents/add") && (
                                <ActionButton
                                  to={`/student/documents/add?student_id=${data?.id}`}
                                  icon="ri-file-text-line"
                                  label="Documents Add"
                                  full
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ════════════════════════════════════════
                          RIGHT — Tabs + Content
                      ════════════════════════════════════════ */}
                      <div className="col-xl-9 col-lg-8 col-md-7">
                        {/* Tab navigation */}
                        <div className="card border-0 shadow-sm mb-3 p-2" style={{ borderRadius: "14px" }}>
                          <ul className="nav nav-pills gap-1 flex-wrap">
                            {TABS?.map((tab) => (
                              <li className="nav-item" key={tab.link}>
                                {tab?.isRedirect ? (
                                  <Link
                                    to={tab.link}
                                    className="nav-link btn btn-sm btn-outline-primary px-3 py-2 d-flex align-items-center"
                                    style={{ borderRadius: "10px" }}
                                  >
                                    <i className={`${tab.icon} ri-18px me-2`} />
                                    {tab.label}
                                  </Link>
                                ) : (
                                  <button
                                    className={`btn btn-sm px-3 py-2 d-flex align-items-center fw-semibold ${filters?.type === tab.link ? "btn-primary" : "btn-outline-primary"
                                      }`}
                                    style={{ borderRadius: "10px" }}
                                    onClick={() => setFilters({ ...filters, type: tab.link })}
                                  >
                                    <i className={`${tab.icon} ri-18px me-2`} />
                                    {tab.label}
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Tab content panel */}
                        <div>
                          {filters?.type === "BasicDetails" && (
                            <BasicDetailsV4
                              data={data}
                              handleFunction={getDataList}
                              setFilters={setFilters}
                            />
                          )}
                          {filters?.type === "CourseNFees" && (
                            <CourseNFees data={data} setLoading={setLoading} handleFunction={getDataList} />
                          )}
                          {filters?.type === "MarkReport" && <MarkReports data={data} />}
                          {filters?.type === "Transactions" && (
                            <FeeTransactions data={data} setLoading={setLoading} handleFunction={getDataList} />
                          )}
                          {filters?.type === "Attendance" && (
                            <AttendanceCalendar
                              attendances={data?.attendances}
                              leave={data?.applied_leave_request}
                              setFilters={setFilters}
                            />
                          )}
                          {filters?.type === "AdmissionForm" ? (
                            <StudentAdmissionForm data={data} />
                          ) : (
                            filters?.type === "DemoForm" && <StudentDemoForm data={data} />
                          )}
                          {filters?.type === "Hostel" && hostelData && (
                            <CourseNFeeTab data={hostelData} getStudent={getHostelData} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
